import { FC, Fragment } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { CommonStyle, ControlledForm, Loader } from "Components";
import CstmBtn from "Components/CstmBtn";
import NoData from "Components/NoData";
import { FormatOrgListType } from "DataTypes/PracticeMangement.type";
import ImgPath from "Constant/Imgs";
import { getPracticeInsuranceCount } from "./Format";
import { StatusPill } from "./PracticeInsuranceCommon";

export interface PracticeListingSectionProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  practiceSearch: string;
  onPracticeSearchChange: (value: string) => void;
  loading: boolean;
  practices: FormatOrgListType[];
  selectedPractice: FormatOrgListType | null;
  onSelectPractice: (practice: FormatOrgListType) => void;
  insuranceCount: number;
  onAddPractice: () => void;
}

const PracticeListingSection: FC<PracticeListingSectionProps> = (props) => {
  const {
    collapsed,
    onToggleCollapsed,
    practiceSearch,
    onPracticeSearchChange,
    loading,
    practices,
    selectedPractice,
    onSelectPractice,
    insuranceCount,
    onAddPractice,
  } = props;

  return (
    <Box sx={{ ...sxSidebar(collapsed) }}>
      <Box
        sx={{
          ...CommonStyle.sxRow,
          justifyContent: collapsed ? "center" : "space-between",
          mb: collapsed ? 1 : 1.5,
        }}
      >
        {!collapsed && (
          <Typography variant="subtitle1" fontWeight="bold">
            Select Practice
          </Typography>
        )}
        <Tooltip
          title={collapsed ? "Expand practices" : "Collapse practices"}
          arrow
        >
          <IconButton size="small" color="primary" onClick={onToggleCollapsed}>
            {collapsed ? (
              <ImgPath.ChevronRightIcon />
            ) : (
              <ImgPath.ChevronLeftIcon />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      {!collapsed && (
        <ControlledForm.InputApi
          id="practice-search"
          placeholder="Search practice"
          inputVal={practiceSearch}
          inputChange={(val) =>
            onPracticeSearchChange(typeof val == "string" ? val : "")
          }
          iconId="search"
        />
      )}
      <Box sx={{ ...sxPracticeList }}>
        {loading ? (
          <Loader />
        ) : practices.length == 0 ? (
          <NoData />
        ) : (
          practices.map((practice) => {
            const selected = selectedPractice?.id == practice.id;
            return (
              <Tooltip
                key={practice.id}
                title={collapsed ? practice.name : ""}
                arrow
              >
                <Box
                  sx={{
                    ...sxPracticeCard(collapsed),
                    ...(selected && sxSelected),
                  }}
                  onClick={() => onSelectPractice(practice)}
                >
                  <ImgPath.BusinessIcon color="primary" />
                  {!collapsed && (
                    <Fragment>
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          sx={sxWrapText}
                        >
                          {practice.name}
                        </Typography>
                        <Typography variant="subtitle2">
                          {`${getPracticeInsuranceCount(
                            practice,
                            selectedPractice,
                            insuranceCount
                          )} Insurances`}
                        </Typography>
                      </Box>
                      <StatusPill status={practice.status || ""} />
                    </Fragment>
                  )}
                </Box>
              </Tooltip>
            );
          })
        )}
      </Box>
      {!collapsed && (
        <CstmBtn
          label="Add New Practice"
          btnType="border"
          width="100%"
          onClick={onAddPractice}
        />
      )}
    </Box>
  );
};

export default PracticeListingSection;

const sxSidebar = (collapsed: boolean) => ({
  ...CommonStyle.sxWhiteBox,
  p: collapsed ? 0.6 : 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  transition: "padding 0.2s ease",
});

const sxPracticeList = {
  flex: 1,
  overflow: "auto",
  my: 0.75,
  pr: 0.5,
};

const sxPracticeCard = (collapsed: boolean) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  gap: collapsed ? 0 : 0.75,
  border: "1px solid",
  borderColor: "custom.main",
  borderRadius: 1,
  p: collapsed ? 0.6 : 0.75,
  mb: 0.5,
  cursor: "pointer",
  minHeight: collapsed ? 38 : "auto",
});

const sxSelected = {
  borderColor: "secondary.main",
  backgroundColor: "rgba(255, 87, 34, 0.18)",
  boxShadow: "0 0 0 1px rgba(255, 87, 34, 0.24)",
};

const sxWrapText = {
  whiteSpace: "normal",
  overflowWrap: "anywhere",
};
